import React, { useState, useEffect } from 'react';
import {
    Paper, Typography, Box, TextField, CircularProgress,
    Grid, Stack
} from '@mui/material';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import DataTable from './DataTable';

// Helper for graph time formatting: show only HH:mm
const formatHourMinute = (dateTime) => {
    if (!dateTime) return '';
    if (dateTime.includes(' ')) {
        const time = dateTime.split(' ')[1] || '';
        const [hh, mm] = time.split(':');
        return `${hh}:${mm}`;
    }
    if (dateTime.includes('T')) {
        const t = dateTime.split('T')[1];
        if (t) {
            const [hh, mm] = t.split(':');
            return `${hh}:${mm}`;
        }
    }
    return dateTime;
};

// Helper for tooltip: show full date and time in "YYYY-MM-DD HH:mm:ss" format
const formatFullDateTime = (dateTime) => {
    if (!dateTime) return '';
    if (dateTime.includes(' ')) {
        // Already in "YYYY-MM-DD HH:mm:ss"
        return dateTime;
    }
    if (dateTime.includes('T')) {
        // ISO format
        return dateTime.replace('T', ' ').split('.')[0];
    }
    return dateTime;
};

const FEED_UNIT = 'Feed Flow (m³)';
const COND_UNIT = 'Cond Flow (m³)';
const POWER_FLOW_UNIT = 'Power Consumption (kW)';
const POWER_CONS_UNIT = 'Power Consumption (kW)';

const FEED_COLOR = '#82ca9d';
const COND_COLOR = '#ffc658';
const POWER_FLOW_COLOR = '#8884d8';
const POWER_CONS_COLOR = '#ff7300';

const DateRangeView = ({ tableName }) => {
    const [dateRange, setDateRange] = useState({
        startDate: '',
        endDate: ''
    });
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const handleDateChange = (type) => (event) => {
        setDateRange(prev => ({
            ...prev,
            [type]: event.target.value
        }));
    };

    useEffect(() => {
        if (!dateRange.startDate || !dateRange.endDate) return;

        // Ensure start is 00:00:00 and end is 23:59:59 for the selected dates
        const startDateTime = `${dateRange.startDate} 00:00:00`;
        const endDateTime = `${dateRange.endDate} 23:59:59`;

        const fetchData = async () => {
            setLoading(true);
            try {
                const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
                const response = await fetch(
                    `${apiBaseUrl}/${tableName}/customrange?startDate=${encodeURIComponent(startDateTime)}&endDate=${encodeURIComponent(endDateTime)}`
                );
                if (!response.ok) throw new Error('Failed to fetch data');
                const result = await response.json();
                setData(result);
            } catch (err) {
                // No setError, just ignore or handle as needed
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [tableName, dateRange]);

    // Always show these three fields if present in data and key exists
     const getDataKeys = () => {
        if (!data.length) return [];
        const keys = Object.keys(data[0]);
        let feedKey, condKey, powerFlowKey, powerConsKey;
        if (tableName.toLowerCase() === 'laxmi-organics') {
            feedKey = 'FEED_FLOW_TOT_CUR_1H_LAXMI3';
            condKey = 'COND_FLOW_TOT_CUR_1H_LAXMI3';
            powerFlowKey = 'EM_POWER_TOT_CUR_1H_LAXMI3';
            powerConsKey = null;
        } else if (tableName.toLowerCase() === 'ks-textile') {
            feedKey = 'FEED_FLOW_PRE_1H_TOT_KS';
            condKey = 'COND_FLOW_PRE_1H_TOT_KS';
            powerFlowKey = 'POWER_PRE_1H_TOT_KS';
            powerConsKey = null;
        } else if (tableName.toLowerCase() === 'clear-choice') {
            feedKey = 'FEED_FLOW_TOT_PRE_1H_CCHOICE';
            condKey = 'COND_FLOW_TOT_PRE_1H_CCHOICE';
            powerFlowKey = 'EM_POWER_TOT_PRE_1H_CCHOICE';
            powerConsKey = null;
        } else if (tableName.toLowerCase() === 'baddi') {
            feedKey = null;
            condKey = null;
            powerFlowKey = 'Baddi_Power_PRE_1h_TOT';
            powerConsKey = 'EM2_POWER_PRE_1H_TOT_HMI';
        } else if (tableName.toLowerCase() === 'parle') {
            feedKey = 'FEED_FLOW_PRE_1H_TOT_PARLE';
            condKey = 'COND_FLOW_TOT_PRE_1HR_PARLE';
            powerFlowKey = 'POWER_PRE_1H_TOT_PARLE';
            powerConsKey = null;
        } else {
            feedKey = keys.find(k => k.toUpperCase().includes('FEED'));
            condKey = keys.find(k => k.toUpperCase().includes('COND'));
            powerFlowKey = keys.find(k =>
                k.toUpperCase().includes('POWER') &&
                !k.toUpperCase().includes('CONSUM') &&
                !k.toUpperCase().includes('EM_POWER_PRE_1HR') &&
                !k.toUpperCase().includes('EM_POWER_PRE_24HR')
            );
            powerConsKey = keys.find(k =>
                k.toUpperCase().includes('CONSUM') ||
                k.toUpperCase().includes('EM_POWER_PRE_1HR') ||
                k.toUpperCase().includes('EM_POWER_PRE_24HR')
            );
        }
        return [
            feedKey && {
                key: feedKey,
                name: FEED_UNIT,
                color: FEED_COLOR,
                yAxisId: 'feed'
            },
            condKey && {
                key: condKey,
                name: COND_UNIT,
                color: COND_COLOR,
                yAxisId: 'cond'
            },
            powerFlowKey && {
                key: powerFlowKey,
                name: POWER_FLOW_UNIT,
                color: POWER_FLOW_COLOR,
                yAxisId: 'powerflow'
            },
            powerConsKey && {
                key: powerConsKey,
                name: POWER_CONS_UNIT,
                color: POWER_CONS_COLOR,
                yAxisId: 'powercons'
            }
        ].filter(Boolean);
    };

    // Memoize keys for tooltip formatter
    const keysMemo = React.useMemo(() => {
        if (!data.length) return {};
        const keys = Object.keys(data[0]);
        if (tableName.toLowerCase() === 'laxmi-organics') {
            return {
                feedKey: 'FEED_FLOW_TOT_CUR_1H_LAXMI3',
                condKey: 'COND_FLOW_TOT_CUR_1H_LAXMI3',
                powerFlowKey: 'EM_POWER_TOT_CUR_1H_LAXMI3',
                powerConsKey: null
            };
        }
        if (tableName.toLowerCase() === 'ks-textile') {
            return {
                feedKey: 'FEED_FLOW_PRE_1H_TOT_KS',
                condKey: 'COND_FLOW_PRE_1H_TOT_KS',
                powerFlowKey: 'POWER_PRE_1H_TOT_KS',
                powerConsKey: null
            };
        }
        if (tableName.toLowerCase() === 'clear-choice') {
            return {
                feedKey: 'FEED_FLOW_TOT_PRE_1H_CCHOICE',
                condKey: 'COND_FLOW_TOT_PRE_1H_CCHOICE',
                powerFlowKey: 'EM_POWER_TOT_PRE_1H_CCHOICE',
                powerConsKey: null
            };
        }
        if (tableName.toLowerCase() === 'baddi') {
            return {
                feedKey: null,
                condKey: null,
                powerFlowKey: 'Baddi_Power_PRE_1h_TOT',
                powerConsKey: 'EM2_POWER_PRE_1H_TOT_HMI'
            };
        }
        if (tableName.toLowerCase() === 'parle') {
            return {
                feedKey: 'FEED_FLOW_PRE_1H_TOT_PARLE',
                condKey: 'COND_FLOW_TOT_PRE_1HR_PARLE',
                powerFlowKey: 'POWER_PRE_1H_TOT_PARLE',
                powerConsKey: null
            };
        }
        return {
            feedKey: keys.find(k => k.toUpperCase().includes('FEED')),
            condKey: keys.find(k => k.toUpperCase().includes('COND')),
            powerFlowKey: keys.find(k =>
                k.toUpperCase().includes('POWER') &&
                !k.toUpperCase().includes('CONSUM') &&
                !k.toUpperCase().includes('EM_POWER_PRE_1HR') &&
                !k.toUpperCase().includes('EM_POWER_PRE_24HR')
            ),
            powerConsKey: keys.find(k =>
                k.toUpperCase().includes('CONSUM') ||
                k.toUpperCase().includes('EM_POWER_PRE_1HR') ||
                k.toUpperCase().includes('EM_POWER_PRE_24HR')
            )
        };
    }, [data, tableName]);

    // Helper to get display name for table
    const getDisplayName = (name) => {
        if (!name) return '';
        if (name.toLowerCase() === 'dr-reddy') return 'DR REDDY 4T';
        if (name.toLowerCase() === 'dr-reddy-sez') return 'DR REDDY SEZ';
        if (name.toLowerCase() === 'laxmi-organics') return 'LAXMI ORGANICS';
        if (name.toLowerCase() === 'ks-textile') return 'KS TEXTILE';
        if (name.toLowerCase() === 'clear-choice') return 'CLEAR CHOICE';
        if (name.toLowerCase() === 'baddi') return 'BADDI';
        // ...add more mappings as needed...
        return name.replace(/-/g, ' ').toUpperCase();
    };

    // Get all yAxisIds used in the current data keys
    const getActiveYAxisIds = () => {
        return getDataKeys().map(k => k.yAxisId);
    };

    return (
        <Box sx={{ mt: 3 }}>
            <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>
                            Custom Date Range View
                        </Typography>
                        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                            <TextField
                                type="date"
                                label="Start Date"
                                value={dateRange.startDate}
                                onChange={handleDateChange('startDate')}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ pattern: "\\d{4}-\\d{2}-\\d{2}" }}
                                placeholder="YYYY-MM-DD"
                            />
                            <TextField
                                type="date"
                                label="End Date"
                                value={dateRange.endDate}
                                onChange={handleDateChange('endDate')}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ pattern: "\\d{4}-\\d{2}-\\d{2}" }}
                                placeholder="YYYY-MM-DD"
                            />
                        </Stack>
                        <Typography variant="caption" color="textSecondary">
                            The graph will show data for the selected date range (00:00:00 to 23:59:59).
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        {loading ? (
                            <Box display="flex" justifyContent="center">
                                <CircularProgress />
                            </Box>
                        ) : data.length > 0 ? (
                            <Box sx={{ height: 400 }}>
                                <ResponsiveContainer>
                                    <LineChart data={data}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="DateTime"
                                            tickFormatter={formatHourMinute}
                                            angle={-45}
                                            textAnchor="end"
                                            height={80}
                                        />
                                        {/* Dynamically render YAxis only for active yAxisIds */}
                                        {getActiveYAxisIds().includes('feed') && (
                                            <YAxis
                                                yAxisId="feed"
                                                label={{
                                                    value: FEED_UNIT,
                                                    angle: -90,
                                                    position: "insideLeft",
                                                    style: { textAnchor: 'middle', fontWeight: 600 }
                                                }}
                                                stroke={FEED_COLOR}
                                            />
                                        )}
                                        {getActiveYAxisIds().includes('cond') && (
                                            <YAxis
                                                yAxisId="cond"
                                                orientation="right"
                                                label={{
                                                    value: COND_UNIT,
                                                    angle: 90,
                                                    position: "insideRight",
                                                    style: { textAnchor: 'middle', fontWeight: 600 }
                                                }}
                                                stroke={COND_COLOR}
                                                width={60}
                                                tick={{ dx: 10 }}
                                            />
                                        )}
                                        {getActiveYAxisIds().includes('powerflow') && (
                                            <YAxis
                                                yAxisId="powerflow"
                                                orientation="right"
                                                label={{
                                                    value: POWER_FLOW_UNIT,
                                                    angle: 90,
                                                    position: "outsideRight",
                                                    style: { textAnchor: 'middle', fontWeight: 600 }
                                                }}
                                                stroke={POWER_FLOW_COLOR}
                                                width={60}
                                                tick={{ dx: 50 }}
                                            />
                                        )}
                                        {getActiveYAxisIds().includes('powercons') && (
                                            <YAxis
                                                yAxisId="powercons"
                                                orientation="right"
                                                label={{
                                                    value: POWER_CONS_UNIT,
                                                    angle: 90,
                                                    position: "outsideRight",
                                                    offset: 60,
                                                    style: { textAnchor: 'middle', fontWeight: 600 }
                                                }}
                                                stroke={POWER_CONS_COLOR}
                                                width={60}
                                                tick={{ dx: 90 }}
                                            />
                                        )}
                                        <Tooltip
                                            labelFormatter={formatFullDateTime}
                                            formatter={(value, name, props) => {
                                                // Show "Field Name : value"
                                                if (props && props.dataKey) {
                                                    const key = props.dataKey;
                                                    if (key === keysMemo.feedKey) {
                                                        return [`Feed Flow : ${Number(value).toFixed(2)}`];
                                                    }
                                                    if (key === keysMemo.condKey) {
                                                        return [`Cond Flow : ${Number(value).toFixed(2)}`];
                                                    }
                                                    if (key === keysMemo.powerFlowKey) {
                                                        return [`Power Consumption : ${Number(value).toFixed(2)}`];
                                                    }
                                                    if (key === keysMemo.powerConsKey) {
                                                        return [`Power Consumption : ${Number(value).toFixed(2)}`];
                                                    }
                                                }
                                                return [`${value}`];
                                            }}
                                        />
                                        <Legend />
                                        {getDataKeys().map(({ key, name, color, yAxisId }) => (
                                            <Line
                                                key={key}
                                                type="monotone"
                                                dataKey={key}
                                                name={name}
                                                stroke={color}
                                                dot={false}
                                                strokeWidth={2}
                                                yAxisId={yAxisId}
                                            />
                                        ))}
                                    </LineChart>
                                </ResponsiveContainer>
                            </Box>
                        ) : (
                            <Typography color="textSecondary">
                                Please select a date range to view data
                            </Typography>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            {data.length > 0 && (
                <Paper sx={{ p: 3 }}>
                    <DataTable
                        data={data}
                        tableName={getDisplayName(tableName)}
                        loading={loading}
                    />
                </Paper>
            )}
        </Box>
    );
};

export default DateRangeView;
